import { useState, useEffect } from "react";
import { Link } from "react-router-dom";

const STORAGE_KEY = "yollo_cookie_consent";

function grantConsent() {
  if (typeof window.gtag === "function") {
    window.gtag("consent", "update", {
      analytics_storage: "granted",
      ad_user_data: "granted",
      ad_personalization: "granted",
      functionality_storage: "granted",
      personalization_storage: "granted",
    });
    // Registra la page view corrente (non tracciata prima del consenso)
    window.gtag("event", "page_view", {
      page_path: window.location.pathname + window.location.search,
      page_title: document.title,
    });
  }
  if (typeof window.posthog !== "undefined" && window.posthog.opt_in_capturing) {
    window.posthog.opt_in_capturing();
  }
}

function denyConsent() {
  if (typeof window.gtag === "function") {
    window.gtag("consent", "update", {
      analytics_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
      functionality_storage: "denied",
      personalization_storage: "denied",
    });
  }
  if (typeof window.posthog !== "undefined" && window.posthog.opt_out_capturing) {
    window.posthog.opt_out_capturing();
  }
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      // Nessuna scelta — mostra il banner dopo un breve ritardo
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    }
    // Consenso già dato: ripristina la scelta
    if (stored === "accepted") grantConsent();
    else denyConsent();
  }, []);

  // Quando il banner è visibile, aggiungi padding-bottom al body per non coprire i pulsanti
  // delle pagine (es. "Avanti" nella registrazione).
  useEffect(() => {
    if (visible) {
      document.body.style.paddingBottom = "180px";
    } else {
      document.body.style.paddingBottom = "";
    }
    return () => {
      document.body.style.paddingBottom = "";
    };
  }, [visible]);

  const handleAccept = () => {
    localStorage.setItem(STORAGE_KEY, "accepted");
    grantConsent();
    setVisible(false);
  };

  const handleReject = () => {
    localStorage.setItem(STORAGE_KEY, "rejected");
    denyConsent();
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-24 sm:bottom-0 left-0 right-0 z-40 p-2 sm:p-4 pointer-events-none"
      style={{ animation: "slideUpBanner 0.35s ease-out" }}
      data-testid="cookie-consent-banner"
    >
      <div className="max-w-4xl mx-auto bg-gray-900 text-white rounded-2xl shadow-2xl border border-gray-700 overflow-hidden pointer-events-auto">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 p-3 sm:p-5">
          {/* Icona */}
          <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-[#6C63FF]/20 flex items-center justify-center text-xl">
            🍪
          </div>

          {/* Testo */}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-white mb-1">
              Utilizziamo i cookie
            </p>
            <p className="text-xs text-gray-400 leading-relaxed">
              Usiamo cookie tecnici (necessari) e, con il tuo consenso, cookie analitici di Google Analytics 4 per migliorare il servizio.
              {" "}
              <Link to="/cookie-policy" className="underline text-[#a89dff] hover:text-white whitespace-nowrap" onClick={() => setVisible(false)} data-testid="cookie-policy-link">
                Cookie Policy
              </Link>
              {" "}·{" "}
              <Link to="/privacy" className="underline text-[#a89dff] hover:text-white whitespace-nowrap" onClick={() => setVisible(false)} data-testid="privacy-policy-link">
                Privacy Policy
              </Link>
            </p>
          </div>

          {/* Pulsanti */}
          <div className="grid grid-cols-2 sm:flex items-center gap-2 flex-shrink-0 w-full sm:w-auto">
            <button
              onClick={handleReject}
              className="min-w-0 whitespace-nowrap px-2 sm:px-4 py-2 rounded-xl text-xs font-semibold border border-gray-600 text-gray-300 hover:bg-gray-700 hover:text-white transition-colors"
              data-testid="cookie-reject-btn"
            >
              Solo necessari
            </button>
            <button
              onClick={handleAccept}
              className="min-w-0 whitespace-nowrap px-2 sm:px-4 py-2 rounded-xl text-xs font-bold text-white transition-all hover:opacity-90 active:scale-95"
              style={{ background: "linear-gradient(135deg, #6C63FF, #8B85FF)" }}
              data-testid="cookie-accept-btn"
            >
              Accetta tutti
            </button>
          </div>
        </div>
      </div>

      <style>{`
        /* Il consenso rimane in sospeso, ma non copre i comandi dei dialoghi. */
        body:has([role="dialog"][data-state="open"]) [data-testid="cookie-consent-banner"] {
          visibility: hidden;
        }
        @keyframes slideUpBanner {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
